/* eslint-disable max-lines -- if you need to modify this code, fix this first */
import { Injector } from '@angular/core';
import { flatten, forOwn, times, values } from '@sama/micro-dash';
import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  CircleGeometry,
  Color,
  DoubleSide,
  Event,
  Intersection,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';
import { Axis } from '../../renderers/side-view/side-view.component';
import { CUBE_MIN_LEGAL_SCALE } from '../../state/cuboid-location-state-3d';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import {
  buildVectorRotatedBetweenSpaces,
  buildVectorRotatedToObjectSpace,
  getMouseVector,
} from '../../utils/point-cloud-utils';
import { mapVector } from '../../utils/point-utils';
import { GlobalRenderTriggerService } from '../global-render-trigger.service';
import { getSideViewRendererLayers, setRendererLayers } from '../render-utils';

const CIRCLE_RADIUS = 0.035;

const CIRCLE_COLOR = new Color(0xaaaaaa);
const TRIANGLE_COLOR = new Color(0xffffff);
const HIGHLIGHT_COLOR = new Color(0xffff00);

const MINIMUM_SCALE = 0.01;

export enum Side {
  Left = 'left',
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
}

export interface EndEvent extends Event {
  type: 'complete';
  side: Side;
}

interface Triangle extends Mesh {
  material: MeshBasicMaterial;
}

interface Circle extends Mesh {
  material: MeshBasicMaterial;
}

export interface Arrow extends Circle {
  children: Triangle[];
  userData: { side: Side };
}

export interface Plane extends Mesh {
  material: MeshBasicMaterial;
}

export class SideViewResizeControls extends Object3D {
  // Need to make this public so we can add the camera to it
  plane!: Plane;
  arrows!: { [side in Side]: Arrow };
  domElement!: HTMLElement;
  target?: Mesh;

  private raycaster = new Raycaster();

  private selectedArrow?: Arrow;
  private highlightedArrow?: Arrow;

  private globalRenderTriggerService: GlobalRenderTriggerService;
  private store: PointCloudContentStore;

  constructor(
    public override visible: boolean,
    public axis: Axis,
    private camera: Camera,
    injector: Injector,
  ) {
    super();
    this.createPlane();
    this.createArrows();
    this.globalRenderTriggerService = injector.get(GlobalRenderTriggerService);
    this.store = injector.get(PointCloudContentStore);
    this.raycaster.layers.set(getSideViewRendererLayers()[this.axis]);
    this.userData = { meshType: 'SideViewResizeControls' };
  }

  private get isResizing() {
    return !!this.selectedArrow;
  }

  override attach(obj: Mesh) {
    if (!this.isAttachable(obj)) {
      throw new Error('Cannot attach side view resize controls to this object');
    }
    this.target = obj;
    this.snapToTarget();
    return this;
  }

  detach() {
    this.target = undefined;

    if (this.isResizing) {
      this.stopResize();
    }
  }

  dispose() {
    this.detach();

    forOwn(this.arrows, (arrow) => {
      arrow.children[0].geometry.dispose();
      arrow.children[0].material.dispose();
      arrow.geometry.dispose();
      arrow.material.dispose();
    });

    this.plane.geometry.dispose();
    this.plane.material.dispose();
  }

  onMouseMove(event: MouseEvent) {
    const mouse = getMouseVector(event, this.domElement);
    this.highlightArrow(mouse);
  }

  isOverControls(event: MouseEvent) {
    const mouse = getMouseVector(event, this.domElement);
    return !!this.getIntersectingArrow(mouse);
  }

  getMouseWorldPoint(event: MouseEvent) {
    const mouse = getMouseVector(event, this.domElement);
    return this.findIntersection(mouse, [this.plane])!.point;
  }

  getArrowAxis() {
    const triangle = this.selectedArrow!.children[0];
    const worldDirection = triangle.getWorldDirection(new Vector3());
    return buildVectorRotatedToObjectSpace(worldDirection, this.target!);
  }

  /**
   * Grow/shrink the target in the same direction the selected triangle is
   * pointing.
   */
  applyResize(delta: Vector3) {
    const arrow = this.selectedArrow!;
    const triangle = arrow.children[0];
    const mesh = this.target!;

    const worldDirection = triangle.getWorldDirection(new Vector3());
    const meshDirection = buildVectorRotatedToObjectSpace(worldDirection, mesh);
    // the percent of the resize delta to apply to each axis
    const axisFilter = mapVector(meshDirection, Math.abs);

    const mouseDelta = buildVectorRotatedToObjectSpace(delta, triangle).z;
    const resizeDelta = this.calcResizeDelta(mouseDelta, axisFilter);
    mesh.scale.addScaledVector(axisFilter, resizeDelta);
    mesh.translateOnAxis(meshDirection, resizeDelta / 2);
    arrow.translateOnAxis(
      buildVectorRotatedToObjectSpace(worldDirection, arrow),
      resizeDelta,
    );
    this.store.updateFocusedLocationFromCuboidMesh(this.target!);
  }

  stopResize() {
    this.selectedArrow = undefined;
  }

  startResize(event: MouseEvent) {
    const mouse = getMouseVector(event, this.domElement);
    this.selectedArrow = this.getIntersectingArrow(mouse);
  }

  getSelectedArrowSide() {
    return this.selectedArrow!.userData.side;
  }

  updateRendererLayers(showArrows: boolean) {
    const layer = showArrows ? [getSideViewRendererLayers()[this.axis]] : [];
    forOwn(this.arrows, (arrow) => setRendererLayers(layer, arrow));
  }

  private createArrows() {
    this.arrows = {} as { [side in Side]: Arrow };
    [Side.Right, Side.Top, Side.Left, Side.Bottom].forEach((side, i) => {
      const angleOnPlane = i * (Math.PI / 2);
      const arrow = this.createArrow(side, angleOnPlane);
      this.plane.add(arrow);
      this.arrows[side] = arrow;
    });
  }

  private createArrow(side: Side, angleOnPlane: number) {
    const arrow = this.createCircle() as Arrow;
    arrow.add(this.createTriangle());
    arrow.rotation.z = angleOnPlane;
    arrow.userData = { side };
    return arrow;
  }

  private createTriangle() {
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new BufferAttribute(this.getTriangleVertices(), 3),
    );
    const triangle = new Mesh(
      geometry,
      new MeshBasicMaterial({ color: TRIANGLE_COLOR, depthTest: false }),
    );

    // lay the triangle down pointing at angle 0 on the plane
    triangle.rotation.y = Math.PI / 2;

    // Ensure controls appear on top
    triangle.renderOrder = 10000001;
    return triangle;
  }

  /**
   * Return vertices in the YZ plane. It is important for the arrow to point in
   * the positive Z direction - that determines which direction the arrow does
   * its resizing.
   */
  private getTriangleVertices() {
    // generate by rotating a point around the X axis
    const xAxis = new Vector3(1, 0, 0);
    const vertex = new Vector3(0, 0, CIRCLE_RADIUS * 0.8);
    const points = times(3, () =>
      vertex.applyAxisAngle(xAxis, -(2 * Math.PI) / 3).toArray(),
    );
    return new Float32Array(flatten(points));
  }

  private createCircle() {
    const geometry = new CircleGeometry(CIRCLE_RADIUS, 128);
    const circle: Circle = new Mesh(
      geometry,
      new MeshBasicMaterial({ color: CIRCLE_COLOR, depthTest: false }),
    );

    // Ensure controls appear on top
    circle.renderOrder = 10000000;
    return circle;
  }

  private createPlane() {
    const geometry = new PlaneGeometry(10000, 10000, 2, 2);
    const material = new MeshBasicMaterial({
      visible: false,
      side: DoubleSide,
      transparent: true,
    });
    this.plane = new Mesh(geometry, material) as Plane;
    this.setPlaneRotation();
    this.add(this.plane);
  }

  private setPlaneRotation() {
    const X = 0;
    const Y = 1;

    switch (this.axis) {
      case X:
        this.plane.rotation.y = -Math.PI / 2;
        this.plane.rotation.z = -Math.PI / 2;
        break;

      case Y:
        this.plane.rotation.x = Math.PI / 2;
        this.plane.rotation.y = Math.PI;
        break;
    }
  }

  private snapToTarget() {
    const mesh = this.target!;

    // center these controls on the target
    mesh.getWorldPosition(this.position);
    mesh.getWorldQuaternion(this.quaternion);
    this.updateMatrixWorld(false);

    // position the arrows around the target
    const meshScaleInPlaneSpace = mapVector(
      buildVectorRotatedBetweenSpaces(mesh.scale, mesh, this.plane),
      Math.abs,
    );
    const arrowScale = this.calcArrowScale(meshScaleInPlaneSpace);
    forOwn(this.arrows, (arrow) => {
      arrow.position.copy(
        this.calcArrowPosition(arrow, meshScaleInPlaneSpace, arrowScale),
      );
      arrow.scale.setScalar(arrowScale);
    });
  }

  /**
   * We grow/shrink the arrows so they stay (roughly) the same size on the
   * screen in the side views when the camera zoom in/out to fit the cuboid.
   */
  private calcArrowScale(meshScaleInPlaneSpace: Vector3) {
    const meshAspect = meshScaleInPlaneSpace.x / meshScaleInPlaneSpace.y;
    const containerAspect =
      this.domElement.clientWidth / this.domElement.clientHeight;

    if (Number.isNaN(containerAspect) || meshAspect < containerAspect) {
      return meshScaleInPlaneSpace.y;
    } else {
      return meshScaleInPlaneSpace.x / containerAspect;
    }
  }

  private calcArrowPosition(
    arrow: Arrow,
    meshScaleInPlaneSpace: Vector3,
    arrowScale: number,
  ) {
    // offset the arrows from (0, 0) the same direction they are pointing
    const direction = buildVectorRotatedToObjectSpace(
      arrow.children[0].getWorldDirection(new Vector3()),
      this.plane,
    );
    const offset = CIRCLE_RADIUS * arrowScale;
    return direction
      .clone()
      .multiply(meshScaleInPlaneSpace)
      .multiplyScalar(0.5)
      .addScaledVector(direction, offset);
  }

  private highlightArrow(mouse: Vector2) {
    // unhighlight last arrow
    if (this.highlightedArrow) {
      this.updateArrowColor(this.highlightedArrow, TRIANGLE_COLOR);
    }

    this.highlightedArrow = this.getIntersectingArrow(mouse);

    if (this.highlightedArrow) {
      this.updateArrowColor(this.highlightedArrow, HIGHLIGHT_COLOR);
    }
  }

  /**
   * This will be the same as `mouseDelta` except when that would make the
   * cuboid smaller than our minimum size (or would turn the cuboid inside-out).
   */
  private calcResizeDelta(mouseDelta: number, axisFilter: Vector3) {
    const mesh = this.target!;
    let magnitude = mouseDelta;
    const axes = ['x', 'y', 'z'] as const;
    for (const axis of axes) {
      if (
        axisFilter[axis] > 1e-10 &&
        mesh.scale[axis] + magnitude * axisFilter[axis] < MINIMUM_SCALE
      ) {
        magnitude = (MINIMUM_SCALE - mesh.scale[axis]) / axisFilter[axis];
      }
    }
    return magnitude;
  }

  private getIntersectingArrow(mouse: Vector2) {
    const intersection = this.findIntersection(mouse, values(this.arrows));
    return intersection && (intersection.object as Arrow);
  }

  private findIntersection(
    point: Vector2,
    objects: Object3D[],
  ): Intersection | undefined {
    this.raycaster.setFromCamera(point, this.camera);
    return this.raycaster.intersectObjects(objects, false)[0];
  }

  private updateArrowColor(arrow: Arrow, color: Color) {
    const material = arrow.children[0].material;
    material.color = color;
    this.globalRenderTriggerService.trigger$.next();
  }

  private isAttachable(obj: Mesh) {
    const tolerance = 0.001;
    return (
      obj.scale.x >= CUBE_MIN_LEGAL_SCALE - tolerance &&
      obj.scale.y >= CUBE_MIN_LEGAL_SCALE - tolerance &&
      obj.scale.z >= CUBE_MIN_LEGAL_SCALE - tolerance
    );
  }
}
